import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import React, {
  useState,
  useImperativeHandle,
  useRef,
  useEffect,
  forwardRef,
} from 'react';

const BillingAddressFormInner = ({ cartStore }, ref) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const storageData = useRef(
    JSON.parse(localStorage.getItem('billingAddress') || '{}'),
  );

  useEffect(() => {
    if (storageData.current) {
      Object.entries(storageData.current).forEach(([key, value]) => {
        cartStore.updateBillingAddress(key, value);
      });
    }
  }, []);

  const handleFieldChange = (field, value) => {
    cartStore.updateBillingAddress(field, value);
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
    validateForm({ showAll: false }); // live validation for that field
  };

  const validateForm = ({ showAll = true } = {}) => {
    const data = cartStore.billingAddress || {};
    const newErrors = {};

    if (!data.email) newErrors.email = 'Email ist erforderlich';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      newErrors.email =
        'Bitte geben Sie eine gültige E-Mail Adresse ein.';
    }

    if (!data.firstname)
      newErrors.firstname = 'Vorname ist erforderlich';
    if (!data.lastname)
      newErrors.lastname = 'Nachname ist erforderlich';
    if (!data.street) newErrors.street = 'Straße ist erforderlich';
    if (!data.house_number)
      newErrors.house_number = 'Hausnummer ist erforderlich';
    if (!data.postcode) newErrors.postcode = 'PLZ ist erforderlich';
    if (!data.city) newErrors.city = 'Stadt ist erforderlich';
    if (!data.country_code || !data.country_code.trim()) {
      newErrors.country_code = 'Land ist erforderlich';
    }

    setErrors(newErrors);
    if (showAll) {
      const allFields = [
        'email',
        'firstname',
        'lastname',
        'street',
        'house_number',
        'postcode',
        'city',
        'country_code',
      ];
      setTouchedFields(
        Object.fromEntries(allFields.map((f) => [f, true])),
      );
    }

    return Object.keys(newErrors).length === 0;
  };

  useImperativeHandle(ref, () => ({
    validate: () => validateForm({ showAll: true }),
  }));

  const errorStyle = {
    color: 'red',
    fontSize: '12pt',
    marginTop: '5px',
  };

  return (
    <>
      <div
        style={{
          fontSize: '15pt',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {t('CONTACT_INFORMATION')}
        <input
          type="email"
          name="gurado_email"
          id="gurado_email"
          placeholder={t('EMAIL')}
          className="gurado-storefront-form-control"
          style={{ height: '50px' }}
          onChange={(e) => handleFieldChange('email', e.target.value)}
          value={cartStore.billingAddress.email || ''}
        />
        {touchedFields.email && errors.email && (
          <div style={errorStyle}>{errors.email}</div>
        )}
      </div>

      <div
        style={{
          fontSize: '15pt',
          marginTop: '20px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {t('BILLING_ADDRESS')}
        <input
          type="text"
          placeholder={t('FIRSTNAME')}
          name="gurado_firstname"
          id="gurado_firstname"
          className="gurado-storefront-form-control"
          style={{ height: '50px' }}
          autoComplete="billing given-name"
          onChange={(e) =>
            handleFieldChange('firstname', e.target.value)
          }
          value={cartStore.billingAddress.firstname || ''}
        />
        {touchedFields.firstname && errors.firstname && (
          <div style={errorStyle}>{errors.firstname}</div>
        )}
      </div>

      <div
        style={{
          fontSize: '15pt',
          marginTop: '20px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <input
          type="text"
          placeholder={t('LASTNAME')}
          style={{ marginTop: '15px', height: '50px' }}
          name="gurado_lastname"
          className="gurado-storefront-form-control"
          onChange={(e) =>
            handleFieldChange('lastname', e.target.value)
          }
          autoComplete="billing family-name"
          value={cartStore.billingAddress.lastname || ''}
          id="gurado_lastname"
        />
        {touchedFields.lastname && errors.lastname && (
          <div style={errorStyle}>{errors.lastname}</div>
        )}
      </div>

      <div
        style={{
          width: '100%',
          display: 'flex',
          marginTop: '15px',
          gap: '5%',
        }}
      >
        <input
          type="text"
          style={{ width: '70%', height: '50px' }}
          placeholder={t('STREET')}
          className="gurado-storefront-form-control"
          onChange={(e) =>
            handleFieldChange('street', e.target.value)
          }
          autoComplete="billing street-address"
          value={cartStore.billingAddress.street || ''}
        />
        <input
          type="text"
          style={{ width: '25%', height: '50px' }}
          placeholder={t('BLOCK_NO')}
          className="gurado-storefront-form-control"
          onChange={(e) =>
            handleFieldChange('house_number', e.target.value)
          }
          value={cartStore.billingAddress.house_number || ''}
        />
      </div>
      {touchedFields.street && errors.street && (
        <div style={errorStyle}>{errors.street}</div>
      )}
      {touchedFields.house_number && errors.house_number && (
        <div style={errorStyle}>{errors.house_number}</div>
      )}

      <input
        type="text"
        style={{ marginTop: '15px', height: '50px' }}
        className="gurado-storefront-form-control"
        placeholder={t('POSTAL_CODE')}
        onChange={(e) =>
          handleFieldChange('postcode', e.target.value)
        }
        value={cartStore.billingAddress.postcode || ''}
      />
      {touchedFields.postcode && errors.postcode && (
        <div style={errorStyle}>{errors.postcode}</div>
      )}

      <input
        type="text"
        placeholder={t('CITY')}
        style={{ marginTop: '15px', height: '50px' }}
        className="gurado-storefront-form-control"
        onChange={(e) => handleFieldChange('city', e.target.value)}
        value={cartStore.billingAddress.city || ''}
      />
      {touchedFields.city && errors.city && (
        <div style={errorStyle}>{errors.city}</div>
      )}

      <select
        style={{ marginTop: '15px', height: '50px' }}
        className="gurado-storefront-form-control"
        onChange={(e) =>
          handleFieldChange('country_code', e.target.value)
        }
        value={cartStore.billingAddress.country_code || ''}
        autoComplete="billing country"
      >
        <option value="">{t('Select country')}</option>
        {cartStore.countries.map((country, idx) => (
          <option value={country.code} key={idx}>
            {country.name}
          </option>
        ))}
      </select>
      {touchedFields.country_code && errors.country_code && (
        <div style={errorStyle}>{errors.country_code}</div>
      )}
    </>
  );
};

const BillingAddressFormWithRef = forwardRef(BillingAddressFormInner);
const BillingAddressForm = observer(BillingAddressFormWithRef);

export default BillingAddressForm;
